import React, { useState, useRef } from 'react';
import { ProFormSelect } from '@ant-design/pro-form';
import { useQuery } from '@apollo/client';
import { Typography, Space, Empty, Spin } from 'antd';
import PropTypes from 'prop-types';
import { GET_USERS_FOR_SELECT_USER } from 'gql/users';
import selectUserOptionItemRender from './selectUserOptionItemRender';
import { displayFacDept, isEmpty } from '../../../utils';

const { Text } = Typography;

const SelectUser = ({ name, initialValues, department, faculty, ...props }) => {
  const { data, loading, refetch } = useQuery(GET_USERS_FOR_SELECT_USER, {
    variables: {
      state: 'ACTIVE',
      ids: initialValues?.[name] ? [initialValues?.[name]] : [],
      first: 1,
    },
  });
  const [isKeywordsEmpty, setIsKeywordsEmpty] = useState(true);
  const isInit = useRef(true);

  if (loading) {
    return (
      <ProFormSelect
        showSearch
        width="xl"
        label="User"
        placeholder="loading..."
        required={true}
        rules={[
          {
            required: true,
          },
        ]}
        fieldProps={{
          loading: true,
          notFoundContent: (
            <Empty
              className="ant-empty-small"
              image={<Spin />}
              description="Loading"
            />
          ),
        }}
      />
    );
  }

  return (
    <ProFormSelect
      showSearch
      width="xl"
      label="User"
      placeholder="type to search user"
      params={{ department, faculty }}
      debounceTime={process.env.REACT_APP_DEBOUNCE_TIME}
      request={async ({ keyWords, department, faculty }) => {
        setIsKeywordsEmpty(isEmpty(keyWords));
        if (isInit.current) {
          isInit.current = false;
          if (!initialValues?.[name]) return [];
          const results = data?.users.map((user) => {
            const { id, name, netId } = user;
            return {
              value: id,
              label: `${name} (${netId})`,
              user,
            };
          });
          return results;
        }
        if (isEmpty(keyWords) && isEmpty(department) && isEmpty(faculty))
          return [];
        const refetchData = await refetch({
          state: 'ACTIVE',
          keyword: keyWords,
          ids: [],
          department: department,
          faculty: faculty,
          first: 10,
        });
        const results = refetchData?.data?.users.map((user) => {
          const { id, name, netId } = user;
          return {
            value: id,
            label: `${name} (${netId})`,
            user,
          };
        });

        return results;
      }}
      rules={[
        {
          required: true,
          message: 'Please select user!',
        },
      ]}
      fieldProps={{
        filterOption: false,
        optionItemRender: ({ user: { department, faculty }, label }) => {
          return (
            <Space direction="vertical">
              {label}
              <Text type="secondary">
                {displayFacDept({
                  department: department,
                  faculty: faculty,
                  prefix: false,
                })}
              </Text>
            </Space>
          );
        },
        notFoundContent: (
          <Empty
            className="ant-empty-small"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={isKeywordsEmpty ? 'Type to Search' : 'No User Found'}
          />
        ),
      }}
      name={name}
      {...props}
    />
  );
};

SelectUser.defaultProps = {
  initialValues: {},
};

SelectUser.propsType = {
  name: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  department: PropTypes.string,
  faculty: PropTypes.string,
};

export default SelectUser;

export { selectUserOptionItemRender };
